import * as React from "react";
import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import * as cx from "classnames";
import {has} from 'lodash';
import {v4 as uuidv4} from 'uuid';
import {LAYOUT_CONSTANTS} from "../const";
import {ArticleContainer, HeaderContainer, SkeletonLoader} from "../components/Containers";
import BackToIndexButton from "../components/BackToIndexButton";
import LeadForm from "../components/LeadForm";
import MainContainer from "../components/MainContainer";
import ArticleTitle from "../components/article/ArticleTitle";
import {ARTICLE_LOADING, ArticleContext} from "../components/article/ArticleCard";
import ArticleIntroduction from "../components/article/ArticleIntroduction";
import ArticleFeaturedImage from "../components/article/ArticleFeaturedImage";
import ArticleMeta from "../components/article/ArticleMeta";
import {ArticleEntity} from "../contract/article";
import useContentApi from "../hooks/useContentApi";
import useCurrentRoute from "../hooks/useCurrentRoute";
import useMedia from "../hooks/useMedia";
import {INITIAL_ROUTE} from "../hooks/useRoutes";
import {setArticle} from "../redux/articleActions";
import {setArticlesLoading} from "../redux/applicationActions";
import {RootState} from "../redux/store";
import XIcon from "../components/XIcon";
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import {TopicEntity} from "../contract/topic";


const {
  CONTENT_MAX_WIDTH,
  FEATURED_IMAGE_WIDTH,
  FORM_ROW_PADDING,
  SMALL_FEATURED_IMAGE_WIDTH
} = LAYOUT_CONSTANTS;

interface Props {
  slug?: string;
  path?: string;
}

const NewsArticleContent: React.FC<Props> = ({
  slug,
}): React.ReactElement => {
  const contentApi = useContentApi();
  const {onSmallMobile, onMobile} = useMedia();
  const [content, setContent] = useState<React.ReactElement>(null);
  const {article} = useSelector((state: RootState) => {
    const articles = Object.values(state.entities.articles.byId);
    return {
      article: (articles || []).find((node: ArticleEntity) => node.slug === slug)
    }
  });

  useEffect(() => {
    if (!contentApi || !article) return;
    let active = true;

    const load = async (): Promise<React.ReactElement|void> => {
      try {
        const {rendered} = await contentApi.queue.add(
          () => contentApi.getArticleContent(article.slug),
          {id: article.id, priority: 3}
        );

        if (!active || !rendered) return;
        return rendered;
      } catch (e) {
        console.error(e.toString());
        return null;
      }
    }

    load().then((result: React.ReactElement): void => {
      if (!result) return; // cancelled request

      setContent(result);
    });

    return (): void => {
      active = false;

      // cancel outstanding subscriptions
      contentApi.cancel(String(article?.id));
      setContent(null);
    }
  }, [article]); // eslint-disable-line react-hooks/exhaustive-deps

  const contentStyles: {[key: string]: number|string} = {margin: '0 auto'};
  if (!onMobile) {
    contentStyles.maxWidth = `${CONTENT_MAX_WIDTH}px`;
  }

  const FEATURED_IMAGE_RATIO = onSmallMobile ? 0.20 : onMobile ? 0.33 : 0.66;

  return (
    <ArticleContainer direction="column" className={"f-5 theme--primary-font-color"} style={contentStyles}>
      <ArticleContext.Provider value={{content: content && article || ARTICLE_LOADING}}>
        <ArticleFeaturedImage
          compact={false}
          columns={1}
          ratio={FEATURED_IMAGE_RATIO}
          height={Math.floor(FEATURED_IMAGE_WIDTH * FEATURED_IMAGE_RATIO)}
          maxWidth={onSmallMobile ? SMALL_FEATURED_IMAGE_WIDTH : FEATURED_IMAGE_WIDTH}
          style={{
            display: 'flex',
            marginTop: FORM_ROW_PADDING,
            marginBottom: `calc(${FORM_ROW_PADDING} * 2)`,
            width: onSmallMobile ? SMALL_FEATURED_IMAGE_WIDTH : FEATURED_IMAGE_WIDTH,
            alignSelf: 'center',
            maxHeight: Math.floor(FEATURED_IMAGE_WIDTH * FEATURED_IMAGE_RATIO)
          }}
          className={"theme--thumbnail-border-radius"}
        />

        {content && article ? (
          <ArticleMeta
            compact={false}
            size={7}
            className={"theme--secondary-color"}
          />
        ) : null}

        {content && article ? (
          <ArticleTitle
            compact={false}
            size={onSmallMobile ? 3 : onMobile ? 2 : 1}
            className={`fw-700 theme--primary-color`}
            style={{marginTop: '5px'}}
          />
        ) : null}

        {content && article ? (
          <ArticleIntroduction
            compact={false}
            size={5}
            style={{
              marginTop: FORM_ROW_PADDING
            }}
            className={"theme--primary-color fw-600"}
          />
        ) : null}
      </ArticleContext.Provider>

      {(!content || !article) ? (
        <SkeletonLoader style={{position: 'relative', flex: 1, marginBottom: FORM_ROW_PADDING}} />
      ) : content}

    </ArticleContainer>
  );
};

const NewsArticle: React.FC<Props> = (): React.ReactElement => {
  const contentApi = useContentApi();
  const pathname = useCurrentRoute();
  const dispatch = useDispatch();
  const {onMobile} = useMedia();
  const [slug, setSlug] = useState<string>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [entry, setEntry] = useState<ArticleEntity>(null);
  const iconSize='32px';

  const {article, articles_visible, base, isStaging, theme} = useSelector((state: RootState) => {
    const articles = Object.values(state.entities.articles.byId);
    const {config, routes, theme} = state.application;
    const {base, environment} = config;
    const route = has(routes, base) ? routes[base] : INITIAL_ROUTE;
    const {articles_visible} = route;

    const isStaging = environment === 'staging';
    return {
      article: !isStaging && (articles || []).find((node: ArticleEntity) => node.slug === slug),
      articles_visible: (articles_visible || []),
      base,
      isStaging,
      theme,
    }
  });

  useEffect(() => {
    if (!base || !pathname) return;

    try {
      setSlug(pathname.split(base)[1].slice(1).replace(/\/$/, ''));
    } catch (e) {
      console.error(`${pathname} could not be resolved!`);
      setSlug(null);
    }
  }, [base, pathname]);

  useEffect(() => {
    if (!contentApi || !slug) return;

    // content from list response
    if (!isStaging && article) {
      if (!entry) setEntry(article);

      return;
    }

    // fetch content
    let active = true;

    const init = async (): Promise<ArticleEntity> => {
      if (!active) return;

      const result = await contentApi.queue.add(
        () => contentApi.getArticle(slug),
        {id: slug, priority: 3}
      );

      if (!result) return;
      return Array.isArray(result) ? result[0] : result;
    };

    init().then((result: any): void => {
      if (!result) return; // cancelled request

      const {nodes, results, ...rest} = result; // eslint-disable-line @typescript-eslint/no-unused-vars
      setEntry(rest);
      dispatch(setArticle(rest));
    });

    return (): void => {
      active = false;

      return;
    }
  }, [article, articles_visible.length, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!entry) return;

    dispatch(setArticlesLoading(pathname, uuidv4(),[String(entry.id)]));
    setLoading(false);
  }, [entry]); // eslint-disable-line react-hooks/exhaustive-deps

  const containerStyles: {[key: string]: number|string} = {width: '100%'};
  if (onMobile) {
    containerStyles.overflowX = 'hidden';
  } else {
    containerStyles.maxWidth = `${CONTENT_MAX_WIDTH}px`;
  }

  const {topics, config} = useSelector((state: RootState) => {
    const topics = Object.values(state.entities.topics.byId);
    const {config} = state.application;

      return {
      topics, config
    }
  });

  const topic = (topics || []).find(
    (topic: TopicEntity) => String(topic.id) === article?.topics?.shift()
  );

  const url = `https://${window.location.hostname}/news/redirect-to?article=${pathname.substr('/news'.length).replace(/\//g, '')}`;
  const description = article?.introduction;

  const linkedIn = config.news_linked_in_enabled
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    ? /* @ts-ignore */ (
      <LinkedinShareButton
        url={url}// URL moet bestaan
        title={article?.title}
        summary={description}
        source={window.location.hostname}
        style={{display: 'inline-block'}}
      >
        <LinkedinIcon round size={iconSize}/>
      </LinkedinShareButton>
    ) : undefined;


  const twitter = config.news_twitter_enabled
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    ? /* @ts-ignore */ (
      <TwitterShareButton
        url={url}
        title={article?.title}
        hashtags={[topic?.slug]}
        style={{display: 'inline-block'}}
      >
        <XIcon size={iconSize}/>
      </TwitterShareButton>
    ) : undefined;

  const facebook = config.news_facebook_enabled
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    ? /* @ts-ignore */ (
      <FacebookShareButton
        url={url}
        title={article?.title}
        hashtag={topic?.slug}
        style={{display: 'inline-block'}}
      >
        <FacebookIcon round size={iconSize}/>
      </FacebookShareButton>
    )
    : undefined;

  return (
    <MainContainer role="main" theme={theme} style={containerStyles} className={cx({'is-loading': loading})}>
      <HeaderContainer style={{justifyContent: 'center'}}>
        <BackToIndexButton />
      </HeaderContainer>
      <NewsArticleContent path={pathname} slug={!loading && slug} />
      <LeadForm loading={loading} />
      <div style={containerStyles}>
        {linkedIn}
        {twitter}
        {facebook}
      </div>
      <div style={containerStyles}>
        <BackToIndexButton style={{width: '100%', display: 'inline-block', verticalAlign: 'top'}}/>
      </div>
    </MainContainer>
  );
};

export default NewsArticle;
